import type { ErrorHandler } from '@angular/core';
import { Injectable, Injector, inject } from '@angular/core';

import { policy } from '../tier';
import type { SnackBarConfig } from '../models/snack-bar.models';
import { CurrentUserService } from '../../user/services/current-user.service';
import { SnackBarService } from '../services/snack-bar/snack-bar.service';
import type { TransKey } from '../services/translate/translate.model';
import { TranslateService } from '../services/translate/translate.service';

import { ErrorReporterService } from './error-reporter.service';
import type { INormalizedHttpError } from './http-error.model';
import { HTTP_ERROR_TRANS_KEY, HttpErrorKind, HttpErrorSilence, readNormalizedHttpError } from './http-error.model';
import { describeFieldErrors, flattenFieldErrors } from './normalize-drf-error.util';
import { contextFor, systematicFailureContext } from './report-context.util';
import { claimStaleChunkReload } from './stale-chunk.util';

/** `SnackBarService.open` dismisses the previous snackbar, so parallel failures would flicker. */
const COALESCE_WINDOW_MS = 1500;
/** Doubled per repeat inside the window: a message that keeps arriving is a loop, not a retry. */
const MAX_COALESCE_WINDOW_MS = 60_000;

/** `instant` answers '' until the locale loads — the exact window a bootstrap request fails in. */
const UNTRANSLATED_FALLBACK = 'Something went wrong.';

/** Neither is actionable: a cross-origin error carries no stack, ResizeObserver's is a hint. */
const IGNORED_MESSAGES = [/ResizeObserver loop/, /Script error\.?$/];

/** `main.ts` passes this to captureConsoleIntegration; the echo below must stay outside it. */
export const CAPTURED_CONSOLE_LEVELS = ['error'];

/** Untranslated on purpose: no customer ever sees this block. */
const INTERNAL_PREFIX = 'internal · ';

/** The detail is several lines and holds the only copy of the event id, so it must not auto-hide. */
const INTERNAL_SNACKBAR_CONFIG: SnackBarConfig = { duration: 0, panelClass: 'snackbar_error_internal' };

const messageOf = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : '';
};

const isIgnored = (message: string): boolean => IGNORED_MESSAGES.some(pattern => pattern.test(message));

@Injectable()
export class LariErrorHandler implements ErrorHandler {
  readonly #snackBar = inject(SnackBarService);
  readonly #translate = inject(TranslateService);
  readonly #reporter = inject(ErrorReporterService);
  readonly #injector = inject(Injector);

  #lastMessage = '';
  #lastShownAt = Number.NEGATIVE_INFINITY;
  #coalesceWindowMs = COALESCE_WINDOW_MS;

  public handleError(error: unknown): void {
    if (!policy.isCustomerFacing) {
      // `warn`, not `error`: internal, qa and staging echo *and* capture, and CAPTURED_CONSOLE_LEVELS
      // is what keeps this from reporting a second time as a message-grouped event.
      // eslint-disable-next-line no-console
      console.warn(error);
    }
    const message = messageOf(error);
    if (isIgnored(message)) {
      return;
    }
    if (claimStaleChunkReload(message)) {
      window.location.reload();
      return;
    }

    const httpError = readNormalizedHttpError(error);
    const eventId = this.#report(error, httpError);

    // Deferred on both paths, so a burst of mixed errors reaches the snackbar in arrival order.
    void eventId.then(id => {
      this.#show(error, httpError, id);
    });
  }

  #report(error: unknown, httpError: INormalizedHttpError | undefined): Promise<string | undefined> {
    if (httpError === undefined || httpError.isReportable) {
      return this.#reporter.report(error, contextFor(httpError));
    }
    const burst = systematicFailureContext(httpError);
    return burst === undefined ? Promise.resolve(undefined) : this.#reporter.report(error, burst);
  }

  #show(error: unknown, httpError: INormalizedHttpError | undefined, eventId: string | undefined): void {
    try {
      const isInternal = this.#isInternalViewer();
      if (httpError !== undefined && !this.#shouldShow(httpError.silence, isInternal)) {
        return;
      }
      const message = this.#messageFor(httpError);
      const detail = isInternal ? this.#internalDetail(error, httpError, message) : '';

      const now = performance.now();
      if (message === this.#lastMessage && now - this.#lastShownAt < this.#coalesceWindowMs) {
        this.#coalesceWindowMs = Math.min(this.#coalesceWindowMs * 2, MAX_COALESCE_WINDOW_MS);
        return;
      }
      this.#lastMessage = message;
      this.#lastShownAt = now;
      this.#coalesceWindowMs = COALESCE_WINDOW_MS;

      const reference = eventId === undefined ? '' : this.#translate.instant('COMMON.ERRORS.REFERENCE', { ref: eventId });
      if (detail.length) {
        const tail = reference.length ? `\n${reference}` : '';
        this.#snackBar.showError(`${message}\n\n${INTERNAL_PREFIX}${detail}${tail}`, undefined, INTERNAL_SNACKBAR_CONFIG);
        return;
      }
      this.#snackBar.showError(reference.length ? `${message} (${reference})` : message);
    } catch {
      // A throw here returns through the window listener as a new error; do not let it loop.
    }
  }

  #shouldShow(silence: HttpErrorSilence, isInternal: boolean): boolean {
    if (silence === HttpErrorSilence.None) {
      return true;
    }
    // A 400 on a read is already reported but has never been visible; surface it while we drain.
    return isInternal && silence === HttpErrorSilence.BadQuery;
  }

  /**
   * Everything a developer needs to reproduce, untruncated. Empty means "do not show a block".
   * `shown` is the line the snackbar already carries: a validation body is the server's own text,
   * so repeating it verbatim under the request line is noise. It still prints when the two differ
   * — the shown line was capped, or the kind fell back to a translated message.
   */
  #internalDetail(error: unknown, httpError: INormalizedHttpError | undefined, shown: string): string {
    if (httpError === undefined) {
      const name = error instanceof Error ? error.name : 'error';
      const message = messageOf(error);
      return message.length && message !== shown ? `${name}: ${message}` : name;
    }
    const head = `${httpError.status} ${httpError.method} ${httpError.url}`;
    const body = describeFieldErrors(httpError.fieldErrors);
    return body.length && body !== shown ? `${head}\n${body}` : head;
  }

  /**
   * `is_staff` reaches Lari staff on the customer tiers. Resolved lazily and guarded
   * because the handler must survive a failure before the user is stored.
   */
  #isInternalViewer(): boolean {
    if (policy.isInternal) {
      return true;
    }
    try {
      return this.#injector.get(CurrentUserService).user.is_staff;
    } catch {
      return false;
    }
  }

  #messageFor(httpError: INormalizedHttpError | undefined): string {
    // Only a write reaches the user with the server's own text; a read's names query params.
    if (httpError?.kind === HttpErrorKind.Validation && httpError.silence === HttpErrorSilence.None) {
      const serverMessage = flattenFieldErrors(httpError.fieldErrors);
      if (serverMessage.length) {
        return serverMessage;
      }
    }
    const key: TransKey = httpError === undefined ? 'COMMON.UNKNOWN_ERROR' : HTTP_ERROR_TRANS_KEY[httpError.kind];
    return this.#translate.instant(key) || UNTRANSLATED_FALLBACK;
  }
}
